/* tslint:disable:max-line-length */
import {Injectable, NgZone} from '@angular/core';
import {MmDbService} from '../../../mm-db/mm-db-service';
import {MmProjectDbCollections} from '../../../mm-db/mm-project-db-schema';
import {FaultPatch} from './fault-patch';
import {resolveSequentially} from '@mydocma/common/src/lib/promise';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {FaultOperationPathEnum} from './fault-operation-path.enum';
import {MmPatchOperation} from '@docma-mm-core/patch/mm-patch-operation';
import {Guid} from 'guid-typescript';
import {Fault, ViewFault} from "@docma-mm-core/fault/fault";
import {MmSyncableModel} from "@docma-mm-core/model/mm-syncable.model";

@Injectable()
export class FaultPatchService {


  constructor(private readonly mmDbService: MmDbService, private ngZone: NgZone) {

  }

  public async save(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const setCommands = faultPatches.map(async (fp) => await db.set(MmProjectDbCollections.fault_patches as never, fp));
      await resolveSequentially(setCommands);
      return faultPatches;
    });
  }

  public async saveAll(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const setCommands = await db.putAll(MmProjectDbCollections.fault_patches as never, faultPatches);
      return faultPatches;
    });
  }

  public async moveToBackupAll(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);

    const allPatches: FaultPatch[] = await db.getAll(MmProjectDbCollections.fault_patches as never)
    const patchesDict = this.groupByOperationId(allPatches);
    const backupPatches: FaultPatch[] = [];

    for (const p of faultPatches) {
      if (patchesDict.hasOwnProperty(p.operation.id)) {
        const mp = patchesDict[p.operation.id];
        backupPatches.push(mp[0]);
      }
    }

    return faultPatches;
  }



  public async remove(mydocmaUid: string, ids: Array<string>): Promise<unknown> {
    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const idb = await dbService.db;
      const transaction = idb.transaction(MmProjectDbCollections.fault_patches as never, 'readwrite');
      return await resolveSequentially(ids.map(id => transaction.store.delete(id)));
    });
  }

  public async removeByFaultUids(mydocmaUid: string, fUids: string[]): Promise<unknown> {
    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const idb = await dbService.db;
      const tx = idb.transaction(MmProjectDbCollections.fault_patches as never, 'readwrite');
      const store = tx.store;
      const index = store.index('fUid');

      const tasks= [];
      for ( let fUid of (fUids || [])) {
        const keys = await index.getAllKeys(fUid);
        tasks.push(... keys.map(key => {
          return store.delete(key)
        }));
      }



      return await resolveSequentially(tasks);
    });
  }



  public async getByFaultUid(mydocmaUid: string, fUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'fUid', fUid);
    return patches.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  public async getByGroupUid(mydocmaUid: string, gUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'gUid', gUid);
    return patches.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  public async getAllKeys(mydocmaUid: string): Promise<Array<IDBValidKey>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    return await db.getAllKeys(MmProjectDbCollections.fault_patches as never);
  }

  public async getAll(mydocmaUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    return await db.getAll(MmProjectDbCollections.fault_patches as never);
  }

  public generateOperationFromChangeState(path: FaultOperationPathEnum, changeState: ChangeState<any>): MmPatchOperation {
    if (!changeState || changeState.status === ChangeStatusEnum.NotChanged) {
      // TODO: Log warnung
      return null;
    }
    if (changeState.status === ChangeStatusEnum.New) {
      return {
        id: Guid.create().toString(),
        op: 'add',
        path,
        value: changeState.data
      };

    } else if (changeState.status === ChangeStatusEnum.Deleted) {
      return {
        id: Guid.create().toString(),
        op: 'remove',
        path,
        value: changeState.data
      };

    } else {
      return {
        id: Guid.create().toString(),
        op: 'replace',
        path,
        value: changeState.data
      };

    }
  }


public groupByOuid(patches: Array<FaultPatch>): { [ouid: string]: Array<FaultPatch> } {
  return patches
    .sort((prev, curr) => +prev?.date - +curr?.date)
    .reduce((acc, curr) => {
      const oUid = curr.oUid;

      if (acc[oUid] == null) {
        acc[oUid] = [];
      }

      acc[oUid].push(curr);

      return acc;
    }, {});
}

  public groupByOperationId(patches: Array<FaultPatch>): { [ouid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const oid = curr.operation.id;

        if (acc[oid] == null) {
          acc[oid] = [];
        }

        acc[oid].push(curr);

        return acc;
      }, {});
  }

public groupByPuid(patches: Array<FaultPatch>): { [puid: string]: Array<FaultPatch> } {
  return patches
    .sort((prev, curr) => +prev?.date - +curr?.date)
    .reduce((acc, curr) => {
      const pUid = curr.pUid;

      if (acc[pUid] == null) {
        acc[pUid] = [];
      }

      acc[pUid].push(curr);

      return acc;
    }, {});
}

  public groupByFuid(patches: Array<FaultPatch>): { [fuid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const fUid = curr.fUid;

        if (acc[fUid] == null) {
          acc[fUid] = [];
        }

        acc[fUid].push(curr);

        return acc;
      }, {});
  }

  public groupByGuid(patches: Array<FaultPatch>): { [fuid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const gUid = curr.gUid;

        if (acc[gUid] == null) {
          acc[gUid] = [];
        }

        acc[gUid].push(curr);

        return acc;
      }, {});
  }

  public groupByAuid(patches: Array<FaultPatch>): { [puid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const aUid = curr.aUid;

        if (acc[aUid] == null) {
          acc[aUid] = [];
        }

        acc[aUid].push(curr);

        return acc;
      }, {});
  }


  public generateChangeStateFromPatches(patches: FaultPatch[]): ChangeState<any> {
    let data = {};
    let status = ChangeStatusEnum.NotChanged;
    let changeDate: Date;
    const ps = [...patches];

    if (ps.length > 0) {
      if (ps[0].operation.op === 'add') {
        status = ChangeStatusEnum.New;
        changeDate = ps[0].date;
        data = ps[0].operation.value;
        ps.splice(0, 1);
      } else if (ps[0].operation.op === 'remove') {
        status = ChangeStatusEnum.Deleted;
        changeDate = patches[ps.length - 1].date;
        data = ps[0].operation.value;
      } else {
        status = ChangeStatusEnum.Changed;
        changeDate = patches[ps.length - 1].date;
      }
    }

    ps.forEach(p => {
      if (p.operation.op === 'add' || p.operation.op === 'replace' ) {
        Object.assign(data, p.operation.value);
      } else if (p.operation.op === 'remove') {
        status = ChangeStatusEnum.Deleted;
      }
    });

    return new ChangeState({
      status,
      date: changeDate,
      data
     });
  }

  public async loadSyncStates(allPatches: FaultPatch[], faults: ViewFault[] | Fault[]): Promise<Fault[]> {
    // console.info('### loadSyncStates 01 ###');

    const groupedPatches: Map<Fault, FaultPatch[]> = new Map<Fault, FaultPatch[]>();
    for (const fault of faults) {
      const f1 = allPatches.filter(x => x.fUid === fault.uid);
      const gUids = f1.filter(x => x.gUid).map(x => x.gUid);
      const f2 = allPatches.filter(x => x.gUid && gUids.includes(x.gUid));

      // const f2 = allPatches.filter(x => x.fUids && x.fUids.includes(fault.uid));

      groupedPatches.set(fault, [...new Set([...f1, ...f2])]);
    }

    for (const fault of faults) {
      if (groupedPatches.has(fault)) {
        const patches = groupedPatches.get(fault);
        const faultPatches = patches.filter(p => (p.operation.path === FaultOperationPathEnum.fault || p.operation.path === FaultOperationPathEnum.faults));
        this.loadSyncChangeState(fault, faultPatches);
        fault.attributeId = fault.data?.Attribute?.Id;
        fault.attachments.forEach(a => {
          const aPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.attachment && p.oUid === a.uid);
          this.loadSyncChangeState(a, aPatches);

          a.scribbles?.forEach(sc => {
            const scPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.attachmentScribbles && p.pUid === a.uid && p.oUid === sc.uid);
            this.loadSyncChangeState(sc, scPatches);
          });
        });

        fault.activities.forEach(activitiy => {
          const acPatches = patches.filter(p => (p.operation.path === FaultOperationPathEnum.activity || p.operation.path === FaultOperationPathEnum.groupActivity) && p.oUid === activitiy.uid);
          this.loadSyncChangeState(activitiy, acPatches);
          activitiy.attachments.forEach(a => {
            const aPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachment && p.oUid === a.uid);
            this.loadSyncChangeState(a, aPatches);

            a.scribbles?.forEach(sc => {
              const scPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachmentScribbles && p.pUid === a.uid && p.oUid === sc.uid);
              this.loadSyncChangeState(sc, scPatches);
            });
          });
        });
      }
    }
    // console.info('### loadSyncStates 02 ###');
    return faults;
  }

  private loadSyncChangeState(syncModel: MmSyncableModel<any>, patches: FaultPatch[]) {

    const syncState = {...syncModel.syncState};
    const syncChangeState = this.generateChangeStateFromPatches(patches);
    syncState.changes = syncChangeState?.status !== ChangeStatusEnum.NotChanged ? [syncChangeState] : [];
    syncModel.syncState = syncState;
  }

}

export function faultUidsOfPatches(allPatches: FaultPatch[]) {
  const patchedFaultUids: string[] = [];

  for (const patch of (allPatches ?? [])) {
    if (patch.fUid && patchedFaultUids.findIndex(x => x === patch.fUid) === -1) {
      patchedFaultUids.push(patch.fUid);
    }
  }
  return patchedFaultUids;
}

export function patchesByFUid(allPatches: FaultPatch[]) {
  const groupedPatches: {[fuid: string]: FaultPatch[]} = {};

  for (const patch of (allPatches ?? [])) {
    if (patch.fUid) {
      if (!groupedPatches.hasOwnProperty(patch.fUid)) {
        groupedPatches[patch.fUid] = [patch];
      }
      else {
        groupedPatches[patch.fUid].push(patch);
      }
    }
  }
  return groupedPatches;
}

export function patchesByGUid(allPatches: FaultPatch[]) {
  const groupedPatches: {[guid: string]: FaultPatch[]} = {};

  for (const patch of (allPatches ?? [])) {
    if (patch.gUid) {
      if (!groupedPatches.hasOwnProperty(patch.gUid)) {
        groupedPatches[patch.gUid] = [patch];
      }
      else {
        groupedPatches[patch.gUid].push(patch);
      }
    }
  }
  return groupedPatches;
}
